'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { Maximize2 } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import { useLandingVideoTracking } from '../hooks/useLandingVideoTracking';
import { VideoModal } from './video-modal';

interface HeroVideoPlayerProps {
  previewSrc: string;
  fullVideoSrc: string;
  posterSrc?: string;
}

export function HeroVideoPlayer({
  previewSrc,
  fullVideoSrc,
  posterSrc = '/landing/user-order.png',
}: HeroVideoPlayerProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const videoCurrentTimeRef = useRef<number>(0);

  const { trackModalOpened, trackModalClosed, getVideoEventHandlers } = useLandingVideoTracking({
    videoId: 'hero-demo',
    section: 'hero',
  });

  const openModal = useCallback(() => {
    trackModalOpened();
    setIsModalOpen(true);
  }, [trackModalOpened]);

  const closeModal = useCallback(() => {
    trackModalClosed(videoCurrentTimeRef.current);
    setIsModalOpen(false);
  }, [trackModalClosed]);

  const handleVideoTimeUpdate = useCallback((currentTime: number) => {
    videoCurrentTimeRef.current = currentTime;
  }, []);

  return (
    <>
      <div
        className="relative w-full h-full cursor-pointer overflow-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={() => setIsHovered((prev) => !prev)}
        onClick={openModal}
      >
        <video
          className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-500"
          autoPlay
          loop
          muted
          playsInline
          poster={posterSrc}
        >
          <source src={previewSrc} type="video/mp4" />
        </video>

        <div
          className={`absolute inset-0 flex items-center justify-center transition-colors duration-300`}
        >
          <AnimatePresence>
            {isHovered && (
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => {
                  e.stopPropagation();
                  openModal();
                }}
                className="w-14 h-14 rounded-full bg-black/10 backdrop-blur-md border border-black/20
                         flex items-center justify-center hover:bg-black/25 hover:border-black/30
                         transition-all duration-300 shadow-2xl hover:scale-110"
                aria-label="Enlarge video"
              >
                <Maximize2 className="w-6 h-6 text-black drop-shadow-lg" />
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>

      <VideoModal
        isOpen={isModalOpen}
        onClose={closeModal}
        videoSrc={fullVideoSrc}
        onTimeUpdate={handleVideoTimeUpdate}
        videoEventHandlers={getVideoEventHandlers()}
      />
    </>
  );
}
